import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Icon from "../components/Icon";
import { useLang } from "../i18n/LanguageContext";
import { PILLARS } from "../data/pillars";

export default function KnowledgeIndex() {
  const { t, pick } = useLang();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title="Knowledge Hub — Tarapith Tantra, Maa Tara & Shakta Traditions"
        description="Explore a deep knowledge ecosystem: Tarapith Tantra, Maa Tara, Dashamahavidya, Shakta Tantra, Tarapith history, festivals, Vedic astrology and more — beginner to advanced."
        path="/knowledge"
        keywords={["Tarapith knowledge", "Tantra guide", "Maa Tara", "Dashamahavidya", "Shakta tradition"]}
      />
      <Breadcrumbs items={[{ label: t("nav_knowledge") }]} />

      <div className="max-w-2xl mb-10">
        <span className="text-xs font-serif tracking-[0.25em] uppercase text-tara-gold/90">{t("pillars_eyebrow")}</span>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-2 mb-3">{t("pillars_title")}</h1>
        <p className="text-tara-muted">{t("pillars_sub")}</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.slug}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
          >
            <Link
              to={`/knowledge/${p.slug}`}
              className={`glass glass-reflect hover-lift group block rounded-2xl overflow-hidden h-full ${p.primary ? "ring-1 ring-tara-gold/40" : ""}`}
              data-testid={`knowledge-card-${p.slug}`}
            >
              <div className="h-44 overflow-hidden relative bg-tara-sand">
                <img src={p.image} alt={pick(p, "title")} className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" loading={i < 3 ? "eager" : "lazy"} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14262A]/75 via-transparent to-transparent" />
                {p.primary && (
                  <span className="absolute top-3 right-3 rounded-full bg-tara-gold/90 px-3 py-1 text-[10px] font-bold text-white tracking-wider uppercase">Core Pillar</span>
                )}
                <span className="absolute bottom-3 left-4 flex h-10 w-10 items-center justify-center rounded-lg bg-white/90 border border-tara-gold/40">
                  <Icon name={p.icon} className="h-5 w-5 text-tara-gold" />
                </span>
              </div>
              <div className="p-6">
                <h2 className="font-display text-2xl font-semibold text-tara-ink mb-2">{pick(p, "title")}</h2>
                <p className="text-sm text-tara-muted leading-relaxed line-clamp-3 mb-4">{pick(p, "summary")}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-serif font-semibold text-tara-gold group-hover:gap-2.5 transition-all">
                  {t("read_more")} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
